/**
 * useAnimations Composable
 *
 * Comprehensive animation system for the Canasta game.
 * Pure Vue 3 composable with reactive state, priority queue, and Web Animations API.
 *
 * Features:
 * - flyCard: Animate card from one position to another using Web Animations API
 * - showExplosion: Particle burst effect with scalable intensity
 * - Priority-based animation queue (immediate > high > normal > low)
 * - Dynamic duration based on distance
 * - Reactive state tracking (isAnimating, queueSize)
 * - Animation speed control (0.5x - 2x)
 * - Reduced motion support (prefers-reduced-motion)
 * - LocalStorage persistence for user preferences
 *
 * @example
 * const { flyCard, showExplosion, isAnimating, animationSpeed } = useAnimations()
 * await flyCard(fromElement, toElement, { duration: 700, easing: 'pickup' })
 * showExplosion(element, { particleCount: 30, color: '#fbbf24' })
 * animationSpeed.value = 1.5 // Speed up animations by 1.5x
 */

import { ref, computed, watch } from 'vue'

/**
 * Animation easing curves
 */
const EASINGS = {
  pickup: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // Slight overshoot, settles
  place: 'cubic-bezier(0.25, 0.1, 0.25, 1)', // Smooth deceleration
  discard: 'cubic-bezier(0.4, 0, 0.2, 1)', // Quick start, soft land
  celebration: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)', // Bouncy
  smooth: 'cubic-bezier(0.25, 0.1, 0.25, 1)', // Default smooth
}

/**
 * Default animation settings
 */
const DEFAULTS = {
  duration: 700,
  minDuration: 500,
  maxDuration: 900,
  staggerDelay: 100,
  cardWidth: 60,
  cardHeight: 84,
}

/**
 * Animation queue for sequential playback
 */
interface QueuedAnimation {
  animationFn: () => Promise<void>
  delayAfter: number
  resolve: (value: void) => void
  priority: 'immediate' | 'high' | 'normal' | 'low'
}

/**
 * Global reactive state (shared across all useAnimations instances)
 */
const animationQueue = ref<QueuedAnimation[]>([])
const isProcessingQueue = ref(false)

// Reactive reduced motion state (updates if user changes OS preference)
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
const reducedMotion = ref(reducedMotionQuery.matches)

// Listen for changes to reduced motion preference
reducedMotionQuery.addEventListener('change', (e) => {
  reducedMotion.value = e.matches
})

// Animation speed multiplier (0.5 = half speed, 2 = double speed)
const animationSpeed = ref(parseFloat(localStorage.getItem('canasta_animation_speed') || '1'))

// Watch animation speed and persist to localStorage
watch(animationSpeed, (newSpeed) => {
  const clamped = Math.max(0.5, Math.min(2, newSpeed))
  if (clamped !== newSpeed) {
    animationSpeed.value = clamped
  } else {
    localStorage.setItem('canasta_animation_speed', String(newSpeed))
  }
})

// Computed: is any animation currently running
const isAnimating = computed(() => isProcessingQueue.value || animationQueue.value.length > 0)

// Computed: number of queued animations
const queueSize = computed(() => animationQueue.value.length)

// Legacy support: check if reduced motion is enabled
const _prefersReducedMotion = reducedMotion.value
void _prefersReducedMotion // Legacy variable for backward compatibility

/**
 * Calculate dynamic duration based on distance
 * Respects animation speed multiplier and reduced motion preference
 */
function calculateDuration(fromX: number, fromY: number, toX: number, toY: number): number {
  if (reducedMotion.value) return 100

  const distance = Math.hypot(toX - fromX, toY - fromY)
  const screenDiag = Math.hypot(window.innerWidth, window.innerHeight)
  const normalized = distance / screenDiag

  const baseDuration =
    DEFAULTS.minDuration + normalized * (DEFAULTS.maxDuration - DEFAULTS.minDuration)

  // Apply animation speed multiplier (faster = shorter duration)
  const adjustedDuration = baseDuration / animationSpeed.value

  return Math.round(adjustedDuration)
}

/**
 * Clear all queued animations
 * Useful for canceling pending animations when game state changes
 */
function clearQueue(): void {
  animationQueue.value = []
  isProcessingQueue.value = false
}

/**
 * Process the animation queue sequentially
 */
async function processQueue(): Promise<void> {
  if (isProcessingQueue.value || animationQueue.value.length === 0) return

  isProcessingQueue.value = true

  while (animationQueue.value.length > 0) {
    const item = animationQueue.value.shift()
    if (!item) break

    try {
      await item.animationFn()

      // Add delay after animation
      if (item.delayAfter > 0) {
        await new Promise((resolve) => setTimeout(resolve, item.delayAfter))
      }

      item.resolve()
    } catch (error) {
      console.error('Animation error:', error)
      item.resolve()
    }
  }

  isProcessingQueue.value = false
}

/**
 * Queue an animation for sequential playback
 */
function queueAnimation(
  animationFn: () => Promise<void>,
  options: {
    priority?: 'immediate' | 'high' | 'normal' | 'low'
    delayAfter?: number
  } = {}
): Promise<void> {
  const { priority = 'normal', delayAfter = 200 } = options

  // Immediate priority bypasses queue
  if (priority === 'immediate') {
    return animationFn()
  }

  return new Promise((resolve) => {
    const item: QueuedAnimation = { animationFn, delayAfter, resolve, priority }

    // Insert based on priority
    if (priority === 'high') {
      const insertIdx = animationQueue.value.findIndex((q) => q.priority !== 'high')
      if (insertIdx === -1) {
        animationQueue.value.push(item)
      } else {
        animationQueue.value.splice(insertIdx, 0, item)
      }
    } else if (priority === 'low') {
      animationQueue.value.push(item)
    } else {
      // Normal priority - insert before low priority items
      const lowIdx = animationQueue.value.findIndex((q) => q.priority === 'low')
      if (lowIdx === -1) {
        animationQueue.value.push(item)
      } else {
        animationQueue.value.splice(lowIdx, 0, item)
      }
    }

    processQueue()
  })
}

/**
 * Get element position on screen
 */
function getElementPosition(element: HTMLElement | null): { x: number; y: number } | null {
  if (!element) return null

  const rect = element.getBoundingClientRect()
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  }
}

/**
 * Create a flying card element
 */
function createFlyingCard(
  fromX: number,
  fromY: number,
  cardData?: { rank?: string; suit?: string }
): HTMLElement {
  const card = document.createElement('div')
  card.className = 'flying-card'
  card.style.cssText = `
    position: fixed;
    left: ${fromX}px;
    top: ${fromY}px;
    width: ${DEFAULTS.cardWidth}px;
    height: ${DEFAULTS.cardHeight}px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    pointer-events: none;
    transform: translate(-50%, -50%);
  `

  // Add card face if provided (using safe DOM methods)
  if (cardData?.rank && cardData?.suit) {
    const content = document.createElement('div')
    content.style.cssText = 'padding: 4px; font-size: 12px;'
    content.textContent = `${cardData.rank} ${cardData.suit}`
    card.appendChild(content)
  }

  document.body.appendChild(card)
  return card
}

/**
 * useAnimations Composable
 */
export function useAnimations() {
  /**
   * Fly a card from one element to another
   *
   * @param fromElement - Source element (or null for offscreen)
   * @param toElement - Target element (or null for offscreen)
   * @param options - Animation options
   * @returns Promise that resolves when animation completes
   */
  async function flyCard(
    fromElement: HTMLElement | null,
    toElement: HTMLElement | null,
    options: {
      duration?: number
      easing?: string
      priority?: 'immediate' | 'high' | 'normal' | 'low'
      cardData?: { rank?: string; suit?: string }
    } = {}
  ): Promise<void> {
    const fromPos = getElementPosition(fromElement)
    const toPos = getElementPosition(toElement)

    if (!fromPos || !toPos) {
      // Can't animate without positions
      return Promise.resolve()
    }

    const duration = options.duration || calculateDuration(fromPos.x, fromPos.y, toPos.x, toPos.y)
    const easing = options.easing || EASINGS.smooth

    const animationFn = async () => {
      return new Promise<void>((resolve) => {
        if (reducedMotion.value) {
          resolve()
          return
        }

        // Create flying card
        const card = createFlyingCard(fromPos.x, fromPos.y, options.cardData)

        // Animate to target
        requestAnimationFrame(() => {
          card.style.transition = `all ${duration}ms ${easing}`
          card.style.left = `${toPos.x}px`
          card.style.top = `${toPos.y}px`
        })

        // Fade out near the end to avoid overlap with rendered card
        setTimeout(() => {
          card.style.transition = `opacity 100ms ease-out`
          card.style.opacity = '0'
        }, duration - 100)

        // Remove card after animation
        setTimeout(() => {
          card.remove()
          resolve()
        }, duration)
      })
    }

    return queueAnimation(animationFn, { priority: options.priority })
  }

  /**
   * Show explosion effect (particle burst)
   *
   * @param element - Element to show explosion at
   * @param options - Explosion options
   */
  function showExplosion(
    element: HTMLElement | null,
    options: {
      particleCount?: number
      duration?: number
      color?: string
    } = {}
  ): void {
    if (!element || reducedMotion.value) return

    // Reduce particle count and duration if reduced motion preference changes mid-session
    const baseParticleCount = reducedMotion.value ? 5 : (options.particleCount ?? 30)
    const baseDuration = reducedMotion.value ? 300 : (options.duration ?? 1000)

    // Apply animation speed multiplier
    const particleCount = baseParticleCount
    const duration = Math.round(baseDuration / animationSpeed.value)
    const { color = '#fbbf24' } = options

    const rect = element.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2

    // Create particle container
    const container = document.createElement('div')
    container.className = 'explosion-container'
    container.style.cssText = `
      position: fixed;
      left: ${centerX}px;
      top: ${centerY}px;
      width: 0;
      height: 0;
      pointer-events: none;
      z-index: 999;
    `
    document.body.appendChild(container)

    // Create particles
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div')
      particle.className = 'explosion-particle'

      const angle = (Math.PI * 2 * i) / particleCount
      const velocity = 50 + Math.random() * 100
      const endX = Math.cos(angle) * velocity
      const endY = Math.sin(angle) * velocity
      const size = 4 + Math.random() * 8

      particle.style.cssText = `
        position: absolute;
        left: 0;
        top: 0;
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border-radius: 50%;
        transform: translate(-50%, -50%);
        transition: all ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
      `

      container.appendChild(particle)

      // Animate particle
      requestAnimationFrame(() => {
        particle.style.transform = `translate(${endX}px, ${endY}px)`
        particle.style.opacity = '0'
        particle.style.width = '0'
        particle.style.height = '0'
      })
    }

    // Remove container after animation
    setTimeout(() => {
      container.remove()
    }, duration + 100)
  }

  /**
   * Show burst effect (similar to explosion but with different behavior)
   *
   * @param element - Element to show burst at
   * @param options - Burst options
   */
  function showBurst(
    element: HTMLElement | null,
    options: {
      particleCount?: number
      duration?: number
      color?: 'green' | 'gold' | 'purple'
    } = {}
  ): void {
    const colorMap = {
      green: '#10b981',
      gold: '#fbbf24',
      purple: '#a855f7',
    }
    const hexColor = options.color ? colorMap[options.color] : colorMap.gold
    showExplosion(element, { ...options, color: hexColor })
  }

  /**
   * Show pulse effect (subtle pulsing animation)
   *
   * @param element - Element to show pulse on
   * @param options - Pulse options
   */
  function showPulse(
    element: HTMLElement | null,
    options: {
      duration?: number
      color?: string
    } = {}
  ): void {
    if (!element || reducedMotion.value) return

    // Apply animation speed multiplier
    const baseDuration = options.duration ?? 500
    const duration = Math.round(baseDuration / animationSpeed.value)
    const { color = '#fbbf24' } = options

    const pulse = document.createElement('div')
    pulse.className = 'pulse-effect'
    pulse.style.cssText = `
      position: fixed;
      width: 100px;
      height: 100px;
      background: radial-gradient(circle, ${color} 0%, transparent 70%);
      border-radius: 50%;
      pointer-events: none;
      z-index: 998;
      opacity: 0.8;
      transition: all ${duration}ms ease-out;
    `

    const rect = element.getBoundingClientRect()
    pulse.style.left = `${rect.left + rect.width / 2 - 50}px`
    pulse.style.top = `${rect.top + rect.height / 2 - 50}px`

    document.body.appendChild(pulse)

    requestAnimationFrame(() => {
      pulse.style.transform = 'scale(1.5)'
      pulse.style.opacity = '0'
    })

    setTimeout(() => {
      pulse.remove()
    }, duration)
  }

  /**
   * Fly multiple cards in sequence
   *
   * @param cards - Array of card data (or null for card backs)
   * @param fromElement - Source element
   * @param toElement - Target element
   * @param faceDown - Whether cards are face down
   * @param stagger - Delay between each card (ms)
   */
  async function flyCards(
    cards: Array<{ rank?: string; suit?: string } | null>,
    fromElement: HTMLElement | null,
    toElement: HTMLElement | null,
    faceDown = false,
    stagger = 100
  ): Promise<void> {
    const promises: Promise<void>[] = []

    for (let i = 0; i < cards.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, stagger))
      const cardData = faceDown ? undefined : (cards[i] || undefined) as
        | { rank?: string; suit?: string }
        | undefined
      promises.push(flyCard(fromElement, toElement, { cardData, priority: 'immediate' }))
    }

    await Promise.all(promises)
  }

  return {
    // Animation functions
    flyCard,
    showExplosion,
    showBurst,
    showPulse,
    flyCards,
    clearQueue,

    // Reactive state
    isAnimating,
    queueSize,
    reducedMotion,
    animationSpeed,
  }
}
